import { useEffect, useRef } from 'react';

/**
 * Cross-tree bridge for "focus this event" requests.
 *
 * The event toast (rendered via `useEventNotification`, which lives ABOVE
 * `MapPanelProvider` in the tree) cannot reach the map/events React context
 * directly. Instead the toast dispatches a `window` CustomEvent, and a listener
 * mounted inside the map subtree reacts to it. This mirrors the existing
 * `eventNotification` window-event pattern already used in this module.
 */
export const FOCUS_EVENT_NAME = 'focusEvent';

export interface FocusEventDetail {
  eventId: string;
}

type FocusCustomEvent = CustomEvent<FocusEventDetail>;

/** Dispatch a request to navigate directly to the given event. */
export const dispatchFocusEvent = (eventId: string): void => {
  window.dispatchEvent(
    new CustomEvent<FocusEventDetail>(FOCUS_EVENT_NAME, {
      detail: { eventId },
    }),
  );
};

/**
 * Subscribe to focus-event requests. Uses the latest-ref pattern so the
 * listener is registered once and always calls the newest handler without
 * re-subscribing on every render.
 */
export const useFocusEventListener = (
  handler: (detail: FocusEventDetail) => void,
): void => {
  const savedHandler = useRef(handler);

  useEffect(() => {
    savedHandler.current = handler;
  });

  useEffect(() => {
    const onFocusEvent = (event: Event) => {
      savedHandler.current((event as FocusCustomEvent).detail);
    };

    window.addEventListener(FOCUS_EVENT_NAME, onFocusEvent);
    return () => window.removeEventListener(FOCUS_EVENT_NAME, onFocusEvent);
  }, []);
};
